import moment from 'moment';
import { toDateISO, todayISO } from '../../utils/dateHelpers';
import { CalendarDay } from '../CalendarDay';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import {
  DaysGrid,
  DayWrapper,
  MonthHeader,
  MonthLabel,
  MonthWrapper,
  NavButton,
  NavSlot,
  WeekdayLabel,
  WeekdayRow,
} from './Calendar.styles';
import type { CalendarProps } from './Calendar.types';

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]].map((d) => d.charAt(0).toUpperCase());
}

function getDefaultMonths(): string[] {
  return moment.localeData().months();
}

function isDateInRange(date: string, start: string, end: string): boolean {
  return date >= start && date <= end;
}

export function Calendar({
  year,
  month,
  availability,
  checkIn,
  checkOut,
  isSelectingCheckOut,
  hoveredDate,
  onDayClick,
  onDayHover,
  onPrevMonth,
  onNextMonth,
  prevMonthDisabled,
  nextMonthDisabled,
  hideWeekdays = false,
  labels: l,
  firstBookableDate,
}: CalendarProps) {
  const today = todayISO();
  const bookableFrom = firstBookableDate && firstBookableDate > today ? firstBookableDate : today;

  const weekdays = l?.weekdays ?? getDefaultWeekdays();
  const monthNames = l?.months ?? getDefaultMonths();

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const firstDayJS = new Date(year, month, 1).getDay();
  const startOffset = (firstDayJS + 6) % 7;

  const rangeStart = checkIn || null;
  const rangeEnd = checkOut || (isSelectingCheckOut && hoveredDate ? hoveredDate : null);

  const cells: React.ReactNode[] = [];

  for (let i = 0; i < startOffset; i++) {
    cells.push(
      <DayWrapper key={`empty-${i}`}>
        <CalendarDay />
      </DayWrapper>
    );
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toDateISO(year, month, d);
    const dayData = availability?.get(dateStr);
    const isPast = dateStr < bookableFrom;
    const isToday = dateStr === today;

    const isCheckIn = dateStr === checkIn;
    const isCheckOut = dateStr === checkOut;
    const hasRange = !!rangeStart && !!rangeEnd && rangeStart !== rangeEnd;
    const isRangeStart = isCheckIn && hasRange;
    const isRangeEnd = hasRange && !isCheckIn && dateStr === rangeEnd;
    const isInRange =
      rangeStart && rangeEnd && !isCheckIn && !isCheckOut && !isRangeEnd
        ? isDateInRange(dateStr, rangeStart, rangeEnd)
        : false;
    const dayOfWeek = (startOffset + d - 1) % 7;
    const isRowStart = dayOfWeek === 0;
    const isRowEnd = dayOfWeek === 6;

    cells.push(
      <DayWrapper
        key={dateStr}
        onMouseEnter={() => onDayHover?.(dateStr)}
        onMouseLeave={() => onDayHover?.(null)}
      >
        <CalendarDay
          day={d}
          dateString={dateStr}
          status={dayData?.type}
          price={dayData?.price}
          currencyCode={dayData?.currencyCode}
          isCheckIn={isCheckIn}
          isCheckOut={isCheckOut}
          isInRange={isInRange}
          isPast={isPast}
          isToday={isToday}
          isRangeStart={isRangeStart}
          isRangeEnd={isRangeEnd}
          isRowStart={isRowStart}
          isRowEnd={isRowEnd}
          isSelectingCheckOut={isSelectingCheckOut}
          onClick={onDayClick}
        />
      </DayWrapper>
    );
  }

  const showNav = !!onPrevMonth || !!onNextMonth;

  return (
    <MonthWrapper>
      <MonthHeader {...(showNav ? undefined : { 'data-no-nav': '' })}>
        {showNav && (
          <NavSlot>
            {onPrevMonth && (
              <NavButton
                type="button"
                onClick={onPrevMonth}
                disabled={prevMonthDisabled}
                aria-label={l?.previousMonth ?? 'Previous month'}
              >
                <ChevronLeftIcon size={16} />
              </NavButton>
            )}
          </NavSlot>
        )}
        <MonthLabel>
          {monthNames[month]} {year}
        </MonthLabel>
        {showNav && (
          <NavSlot>
            {onNextMonth && (
              <NavButton
                type="button"
                onClick={onNextMonth}
                disabled={nextMonthDisabled}
                aria-label={l?.nextMonth ?? 'Next month'}
              >
                <ChevronRightIcon size={16} />
              </NavButton>
            )}
          </NavSlot>
        )}
      </MonthHeader>
      {!hideWeekdays && (
        <WeekdayRow>
          {weekdays.map((wd, i) => (
            <WeekdayLabel key={i}>{wd}</WeekdayLabel>
          ))}
        </WeekdayRow>
      )}
      <DaysGrid>{cells}</DaysGrid>
    </MonthWrapper>
  );
}
